import { Accordion, Badge, Col, Row, Table } from "react-bootstrap";
import AllocationBar from "../components/AllocationBar/AllocationBar";
import CopyButton from "../components/CopyButton";
import { formatCurrency, formatDecimal } from "../lib/numbers";
import { getRiskAllocation } from "./lib";
import type {
  V2CompletionAccountDetail,
  V2CompletionStrategyStep,
} from "./lib/explainV2Types";

interface SoftCompletionExplainProps {
  data: V2CompletionStrategyStep;
  accountNames?: Record<string, string>;
  modelNames?: Record<string, string>;
}

const TABLE_STYLE = { fontSize: "0.85rem" } as const;
const BINDING_THRESHOLD = 1.0;

const toRiskRecord = (risk: Record<string, string>): Record<string, number> => {
  const result: Record<string, number> = {};
  for (const [key, value] of Object.entries(risk)) {
    result[key] = parseFloat(value);
  }
  return result;
};

// --- Budget Ratios Table ---

const BudgetRatiosTable = ({
  budgetRatios,
  householdTargetByAc,
}: {
  budgetRatios: Record<string, string>;
  householdTargetByAc: Record<string, string>;
}) => {
  const rows = Object.entries(budgetRatios)
    .map(([ac, ratio]) => ({
      assetClass: ac,
      ratio: parseFloat(ratio),
      budget: parseFloat(householdTargetByAc[ac] ?? "0"),
    }))
    .sort((a, b) => a.ratio - b.ratio);

  return (
    <Table size="sm" bordered className="mb-0" style={TABLE_STYLE}>
      <thead className="table-light">
        <tr>
          <th>Asset Class</th>
          <th className="text-end">Budget</th>
          <th className="text-end">Ratio</th>
          <th className="text-center">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const binding = row.ratio < BINDING_THRESHOLD;
          return (
            <tr key={row.assetClass}>
              <td>{row.assetClass}</td>
              <td className="text-end">{formatCurrency(row.budget, 2)}</td>
              <td
                className={`text-end${binding ? " text-danger fw-bold" : ""}`}
              >
                {row.ratio.toFixed(2)}
              </td>
              <td className="text-center">
                <Badge
                  bg={binding ? "warning" : "success"}
                  text={binding ? "dark" : undefined}
                >
                  {binding ? "Binding" : "OK"}
                </Badge>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

// --- Model Pipeline Table (Original -> Filtered -> Constrained) ---

const ModelPipelineTable = ({
  originalModel,
  filteredModel,
  constrainedModel,
}: {
  originalModel: Record<string, string>;
  filteredModel: Record<string, string>;
  constrainedModel: Record<string, string>;
}) => {
  const allSymbols = new Set([
    ...Object.keys(originalModel),
    ...Object.keys(filteredModel),
    ...Object.keys(constrainedModel),
  ]);

  const rows = [...allSymbols]
    .map((symbol) => ({
      symbol,
      original: parseFloat(originalModel[symbol] ?? "0"),
      filtered: parseFloat(filteredModel[symbol] ?? "0"),
      constrained: parseFloat(constrainedModel[symbol] ?? "0"),
    }))
    .sort((a, b) => b.original - a.original);

  const totalOriginal = rows.reduce((s, r) => s + r.original, 0);
  const totalFiltered = rows.reduce((s, r) => s + r.filtered, 0);
  const totalConstrained = rows.reduce((s, r) => s + r.constrained, 0);

  return (
    <Table size="sm" bordered className="mb-0" style={TABLE_STYLE}>
      <thead className="table-light">
        <tr>
          <th>Symbol</th>
          <th className="text-end">Original</th>
          <th className="text-end">Filtered</th>
          <th className="text-end">Constrained</th>
          <th className="text-end">Change</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const removed = row.original > 0 && row.filtered === 0;
          const changed = Math.abs(row.constrained - row.filtered) > 0.0001;
          return (
            <tr key={row.symbol} className={removed ? "text-muted" : undefined}>
              <td>
                {row.symbol}
                {removed && (
                  <Badge
                    bg="secondary"
                    className="ms-1"
                    style={{ fontSize: "0.7rem" }}
                  >
                    filtered
                  </Badge>
                )}
              </td>
              <td className="text-end">{formatDecimal(row.original)}</td>
              <td className="text-end">
                {removed ? "—" : formatDecimal(row.filtered)}
              </td>
              <td className="text-end">
                {removed ? "—" : formatDecimal(row.constrained)}
              </td>
              <td
                className={`text-end${
                  removed ? " text-danger" : changed ? " text-warning" : ""
                }`}
              >
                {removed
                  ? `−${formatDecimal(row.original)}`
                  : changed
                    ? formatDecimal(row.constrained - row.filtered)
                    : "—"}
              </td>
            </tr>
          );
        })}
        <tr className="fw-bold">
          <td>Total</td>
          <td className="text-end">{formatDecimal(totalOriginal)}</td>
          <td className="text-end">{formatDecimal(totalFiltered)}</td>
          <td className="text-end">{formatDecimal(totalConstrained)}</td>
          <td></td>
        </tr>
      </tbody>
    </Table>
  );
};

// --- Main Household-Level Component ---

const SoftCompletionExplain = ({
  data,
  accountNames,
  modelNames,
}: SoftCompletionExplainProps) => {
  const householdAum = parseFloat(data.household_aum);
  const verificationPassed = data.verification.passed;

  const sortedAccounts = data.account_classifications.toSorted(
    (a, b) => a.processing_order - b.processing_order,
  );

  // Separate model accounts (soft-constrained) from residual accounts
  const modelAccounts = sortedAccounts.filter((a) => a.has_explicit_model);
  const residualAccounts = sortedAccounts.filter((a) => !a.has_explicit_model);

  const getAccountName = (id: number | string) =>
    accountNames?.[String(id)] || `Account ${id}`;

  return (
    <>
      {/* Section 1: Strategy Overview */}
      <Row className="mb-3">
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <h3 className="mb-0">Soft Completion Strategy</h3>
          <CopyButton
            data={data}
            title="Copy soft completion strategy data to clipboard"
            size={16}
          />
        </div>
      </Row>
      <Row className="mb-3">
        <Col md={6}>
          <Table size="sm" bordered className="mb-0" style={{ width: "auto" }}>
            <tbody>
              <tr>
                <td>
                  <strong>Strategy</strong>
                </td>
                <td>{data.strategy}</td>
              </tr>
              <tr>
                <td>
                  <strong>Household AUM</strong>
                </td>
                <td>{formatCurrency(householdAum, 2)}</td>
              </tr>
              <tr>
                <td>
                  <strong>Model Accounts</strong>
                </td>
                <td>{modelAccounts.length} (soft preferences)</td>
              </tr>
              <tr>
                <td>
                  <strong>Residual Accounts</strong>
                </td>
                <td>{residualAccounts.length}</td>
              </tr>
              <tr>
                <td>
                  <strong>Verification</strong>
                </td>
                <td>
                  <Badge bg={verificationPassed ? "success" : "danger"}>
                    {verificationPassed ? "PASS" : "FAIL"}
                  </Badge>
                  <span
                    className="ms-2 text-muted"
                    style={{ fontSize: "0.85rem" }}
                  >
                    max deviation:{" "}
                    {formatDecimal(parseFloat(data.verification.max_deviation))}
                  </span>
                </td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>

      {/* Section 2: Processing Order */}
      <Row className="mb-3">
        <Col>
          <h5>Processing Order</h5>
          <Table
            size="sm"
            striped
            bordered
            className="mb-0"
            style={TABLE_STYLE}
          >
            <thead className="table-light">
              <tr>
                <th>Order</th>
                <th>Account</th>
                <th>Type</th>
                <th>Model</th>
                <th className="text-end">Balance</th>
                <th className="text-end">HH Weight</th>
              </tr>
            </thead>
            <tbody>
              {sortedAccounts.map((acct) => {
                const balance = parseFloat(acct.balance);
                return (
                  <tr key={acct.account_id}>
                    <td>{acct.processing_order}</td>
                    <td>{getAccountName(acct.account_id)}</td>
                    <td>
                      <Badge bg={acct.has_explicit_model ? "primary" : "info"}>
                        {acct.has_explicit_model ? "Soft Model" : "Residual"}
                      </Badge>
                    </td>
                    <td>
                      {acct.has_explicit_model
                        ? modelNames?.[String(acct.account_id)] ||
                          `Model ${acct.model_id}`
                        : acct.processing_group === "no_model_taxable"
                          ? "None (Taxable)"
                          : "None (Tax Exempt)"}
                    </td>
                    <td className="text-end">{formatCurrency(balance, 2)}</td>
                    <td className="text-end">
                      {householdAum > 0
                        ? formatDecimal(balance / householdAum)
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Col>
      </Row>

      {/* Section 3: Soft Constraint Details (per model account) */}
      {modelAccounts.length > 0 && data.account_details && (
        <Row className="mb-3">
          <Col>
            <h5>Soft Constraint Pipeline</h5>
            <Accordion>
              {modelAccounts.map((acct) => {
                const detail = data.account_details?.[String(acct.account_id)];
                if (!detail?.original_model) return null;
                return (
                  <Accordion.Item
                    key={acct.account_id}
                    eventKey={String(acct.account_id)}
                  >
                    <Accordion.Header>
                      {getAccountName(acct.account_id)}
                      {detail.budget_ratios && (
                        <>
                          {Object.values(detail.budget_ratios).some(
                            (r) => parseFloat(r) < BINDING_THRESHOLD,
                          ) && (
                            <Badge bg="warning" text="dark" className="ms-2">
                              {
                                Object.values(detail.budget_ratios).filter(
                                  (r) => parseFloat(r) < BINDING_THRESHOLD,
                                ).length
                              }{" "}
                              binding
                            </Badge>
                          )}
                        </>
                      )}
                    </Accordion.Header>
                    <Accordion.Body>
                      <h6 className="text-muted mb-2">
                        1. Model Pipeline (Original → Filtered → Constrained)
                      </h6>
                      <div className="mb-3">
                        <ModelPipelineTable
                          originalModel={detail.original_model}
                          filteredModel={detail.filtered_model || {}}
                          constrainedModel={detail.constrained_model || {}}
                        />
                      </div>

                      {detail.budget_ratios && (
                        <>
                          <h6 className="text-muted mb-2">
                            2. Per-AC Budget Ratios
                          </h6>
                          <div className="mb-3">
                            <BudgetRatiosTable
                              budgetRatios={detail.budget_ratios}
                              householdTargetByAc={
                                data.household_target_by_asset_class
                              }
                            />
                          </div>
                        </>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Col>
        </Row>
      )}

      {/* Section 4: Residual Account Calculations */}
      {Object.keys(data.residual_models).length > 0 && (
        <Row className="mb-3">
          <Col>
            <h5>Residual Calculations</h5>
            <p className="text-muted" style={TABLE_STYLE}>
              After soft-constraining model accounts, remaining budget is
              distributed to residual accounts via the waterfall.
            </p>
            <Accordion>
              {Object.entries(data.residual_models).map(
                ([accountId, residualModel]) => (
                  <Accordion.Item key={accountId} eventKey={accountId}>
                    <Accordion.Header>
                      {getAccountName(accountId)}
                      {residualModel.scaled && (
                        <Badge bg="info" className="ms-2">
                          scaled
                        </Badge>
                      )}
                    </Accordion.Header>
                    <Accordion.Body>
                      {residualModel.residual_calculation && (
                        <>
                          <h6 className="text-muted mb-2">
                            Waterfall Target Weights
                          </h6>
                          <Table
                            size="sm"
                            bordered
                            className="mb-3"
                            style={TABLE_STYLE}
                          >
                            <thead className="table-light">
                              <tr>
                                <th>Symbol</th>
                                <th className="text-end">Weight</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(
                                residualModel.residual_target_weights,
                              )
                                .sort(
                                  (a, b) => parseFloat(b[1]) - parseFloat(a[1]),
                                )
                                .map(([symbol, weight]) => (
                                  <tr key={symbol}>
                                    <td>{symbol}</td>
                                    <td className="text-end">
                                      {formatDecimal(parseFloat(weight))}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </Table>
                        </>
                      )}

                      {residualModel.excluded_symbols.length > 0 && (
                        <div className="mt-3">
                          <h6 className="text-muted mb-2">Excluded Symbols</h6>
                          <span style={TABLE_STYLE}>
                            {residualModel.excluded_symbols.join(", ")}
                          </span>
                        </div>
                      )}

                      {residualModel.scaled && residualModel.scale_factor && (
                        <div className="mt-2" style={TABLE_STYLE}>
                          <strong>Scale factor:</strong>{" "}
                          {formatDecimal(
                            parseFloat(residualModel.scale_factor),
                          )}
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                ),
              )}
            </Accordion>
          </Col>
        </Row>
      )}

      {/* Section 5: Verification */}
      <Row className="mb-3">
        <Col>
          <h5>
            Verification{" "}
            <Badge bg={verificationPassed ? "success" : "danger"}>
              {verificationPassed ? "PASS" : "FAIL"}
            </Badge>
          </h5>
          <div className="mb-2" style={TABLE_STYLE}>
            Total deviation:{" "}
            {formatDecimal(parseFloat(data.verification.max_deviation))}
          </div>

          {Object.keys(data.verification.deviations_by_asset_class).length >
            0 && (
            <Table
              size="sm"
              striped
              bordered
              className="mb-3"
              style={TABLE_STYLE}
            >
              <thead className="table-light">
                <tr>
                  <th>Asset Class</th>
                  <th className="text-end">Deviation</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(
                  data.verification.deviations_by_asset_class,
                ).map(([ac, deviation]) => {
                  const deviationNum = parseFloat(deviation);
                  return (
                    <tr key={ac}>
                      <td>{ac}</td>
                      <td
                        className={`text-end${
                          !data.verification.passed &&
                          Math.abs(deviationNum) > 0.0001
                            ? " text-danger fw-bold"
                            : ""
                        }`}
                      >
                        {formatDecimal(deviationNum)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Col>
      </Row>

      {/* Section 6: Summary */}
      {data.summary.length > 0 && (
        <Row className="mb-3">
          <Col>
            <h5>Summary</h5>
            <ul style={TABLE_STYLE}>
              {data.summary.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </Col>
        </Row>
      )}
    </>
  );
};

// --- Per-Account Section (for model accounts in the account-centric view) ---

export const SoftCompletionAccountSection = ({
  detail,
}: {
  detail: V2CompletionAccountDetail;
}) => {
  const pctOfHousehold = parseFloat(detail.pct_of_household);
  const originalModel = detail.original_model;
  // Residual accounts inherit the household target; do not render target bars for them.
  const isResidual = detail.role === "residual";

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center gap-2 mb-3">
        <h5 className="mb-0">Soft Completion</h5>
        <Badge bg="primary">Soft Model</Badge>
        <span className="text-muted" style={{ fontSize: "0.85rem" }}>
          {formatDecimal(pctOfHousehold)} of household
        </span>
      </div>

      {/* Risk allocation bar (household target) — hidden for residual accounts */}
      {!isResidual && (
        <Row className="mb-3">
          <Col>
            <div className="mb-2 d-flex align-items-end">
              <div
                style={{
                  width: "120px",
                  paddingRight: "12px",
                  textAlign: "right",
                }}
              >
                <small className="text-muted">Household</small>
              </div>
              <div style={{ flex: "1" }}>
                <AllocationBar
                  data={toRiskRecord(detail.household_risk)}
                  converter={getRiskAllocation}
                />
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* Model pipeline */}
      {originalModel && (
        <>
          <h6 className="text-muted mb-2">
            Model Pipeline (Original → Filtered → Constrained)
          </h6>
          <div className="mb-3">
            <ModelPipelineTable
              originalModel={originalModel}
              filteredModel={detail.filtered_model || {}}
              constrainedModel={detail.constrained_model || {}}
            />
          </div>

          {detail.budget_ratios && (
            <>
              <h6 className="text-muted mb-2">Per-AC Budget Ratios</h6>
              <div className="mb-3">
                <BudgetRatiosTable
                  budgetRatios={detail.budget_ratios}
                  householdTargetByAc={detail.household_target_by_asset_class}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SoftCompletionExplain;
